import type { BitPalmAgentOptions, BitPalmAgentInstance, BitPalmIdentifyPayload, BitPalmCustomContext, BitPalmEventType, BitPalmEventCallback } from "./types";

const DEFAULT_BASE_URL = "https://agents.bitpalm.ai";
const STORAGE_PREFIX = "_bp_";
const IGNORED_INPUT_TYPES = new Set(["password", "file", "checkbox", "radio", "submit", "button", "reset", "image"]);
const IDENTITY_DEDUPE_WINDOW_MS = 8_000;
const CUSTOM_CONTEXT_MAX_BYTES = 4096;
const IDENTITY_SIGNATURE_KEY = `${STORAGE_PREFIX}identify_signature`;
const IDENTITY_FINGERPRINT_KEY = `${STORAGE_PREFIX}identify_fingerprint`;
const IDENTITY_SENT_AT_KEY = `${STORAGE_PREFIX}identify_sent_at`;

type FormFieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

type IdentityPayload = {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
};

type IdentifyRequestPayload = IdentityPayload & {
  external_user_id?: string;
  signature?: string;
  signed_at?: number;
  signature_version?: "v1";
  identify_source?: "manual" | "form";
};

function buildIdentityFingerprint(payload: IdentifyRequestPayload): string {
  return [
    payload.external_user_id || "",
    payload.email || "",
    payload.phone || "",
    payload.name || "",
    payload.company || "",
    payload.signature || "",
    payload.signed_at != null ? String(payload.signed_at) : "",
    payload.signature_version || "",
    payload.identify_source || "",
  ]
    .join("|")
    .toLowerCase();
}

function normalizeText(value: string | null | undefined, maxLen = 180): string {
  if (!value) return "";
  return value.replace(/\s+/g, " ").trim().slice(0, maxLen);
}

function normalizeTokens(value: string | null | undefined): string {
  if (!value) return "";
  return ` ${value.toLowerCase().replace(/[_-]+/g, " ").replace(/[^a-z0-9\s@.]/g, " ").replace(/\s+/g, " ").trim()} `;
}

function includesAny(haystack: string, needles: string[]): boolean {
  for (const needle of needles) {
    if (haystack.includes(needle)) return true;
  }
  return false;
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value);
}

function normalizePhone(value: string | null | undefined): string {
  const trimmed = normalizeText(value, 50);
  if (!trimmed) return "";
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return "";
  return `${hasPlus ? "+" : ""}${digits.slice(0, 20)}`;
}

function normalizeEmail(value: string | null | undefined): string {
  const normalized = normalizeText(value, 140).toLowerCase();
  if (!normalized) return "";
  if (!isEmail(normalized)) return "";
  return normalized;
}

function getExplicitValue(form: HTMLFormElement, selectors: string[]): string {
  for (const selector of selectors) {
    const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector);
    const value = normalizeText(el?.value);
    if (value) return value;
  }
  return "";
}

function getFieldHint(el: FormFieldElement): string {
  return normalizeText(el.getAttribute("data-bp-field") || el.getAttribute("data-bitpalm-field"), 64)
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
}

function escapeAttrSelectorValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function collectElementTokenParts(form: HTMLFormElement, el: FormFieldElement): string[] {
  const parts: string[] = [];
  const pushText = (value: string | null | undefined, maxLen = 140) => {
    const clean = normalizeText(value, maxLen);
    if (clean) parts.push(clean);
  };

  pushText(el.name);
  pushText(el.id);
  pushText(el.getAttribute("autocomplete"));
  pushText(el.getAttribute("placeholder"));
  pushText(el.getAttribute("aria-label"));
  pushText(el.getAttribute("data-field"));
  pushText(el.getAttribute("data-name"));
  pushText(el.getAttribute("data-label"));
  pushText(el.getAttribute("data-bp-field"));
  pushText(el.getAttribute("data-bitpalm-field"));

  const seenLabels = new Set<string>();
  const pushLabel = (value: string | null | undefined) => {
    const label = normalizeText(value, 200);
    if (!label || seenLabels.has(label)) return;
    seenLabels.add(label);
    parts.push(label);
  };

  for (const label of Array.from(el.labels || [])) {
    pushLabel(label.textContent);
  }

  if (el.id) {
    const forLabel = form.querySelector(`label[for="${escapeAttrSelectorValue(el.id)}"]`);
    pushLabel(forLabel?.textContent);
  }

  const nearestLabel = el.closest("label");
  pushLabel(nearestLabel?.textContent);

  const ariaLabelledBy = el.getAttribute("aria-labelledby");
  if (ariaLabelledBy) {
    for (const id of ariaLabelledBy.split(/\s+/)) {
      if (!id) continue;
      const ref = form.ownerDocument.getElementById(id);
      pushLabel(ref?.textContent);
    }
  }

  return parts;
}

function extractIdentityFromForm(form: HTMLFormElement): IdentityPayload | null {
  let email = normalizeText(getExplicitValue(form, [
    "[data-bp-email]",
    "[data-bitpalm-email]",
  ]), 140).toLowerCase();

  let phone = normalizePhone(getExplicitValue(form, [
    "[data-bp-phone]",
    "[data-bitpalm-phone]",
  ]));

  let name = normalizeText(getExplicitValue(form, [
    "[data-bp-name]",
    "[data-bitpalm-name]",
    "[data-bp-full-name]",
    "[data-bitpalm-full-name]",
  ]));

  let firstName = normalizeText(getExplicitValue(form, [
    "[data-bp-first-name]",
    "[data-bitpalm-first-name]",
  ]));

  let lastName = normalizeText(getExplicitValue(form, [
    "[data-bp-last-name]",
    "[data-bitpalm-last-name]",
  ]));

  let company = normalizeText(getExplicitValue(form, [
    "[data-bp-company]",
    "[data-bitpalm-company]",
  ]));

  for (const rawEl of Array.from(form.elements)) {
    if (!(rawEl instanceof HTMLInputElement || rawEl instanceof HTMLTextAreaElement || rawEl instanceof HTMLSelectElement)) continue;
    if (rawEl.disabled) continue;
    if (rawEl instanceof HTMLInputElement && IGNORED_INPUT_TYPES.has(rawEl.type.toLowerCase())) continue;
    const fieldHint = getFieldHint(rawEl);

    const explicitField =
      rawEl.hasAttribute("data-bp-email") ||
      rawEl.hasAttribute("data-bitpalm-email") ||
      rawEl.hasAttribute("data-bp-phone") ||
      rawEl.hasAttribute("data-bitpalm-phone") ||
      rawEl.hasAttribute("data-bp-name") ||
      rawEl.hasAttribute("data-bitpalm-name") ||
      rawEl.hasAttribute("data-bp-full-name") ||
      rawEl.hasAttribute("data-bitpalm-full-name") ||
      rawEl.hasAttribute("data-bp-first-name") ||
      rawEl.hasAttribute("data-bitpalm-first-name") ||
      rawEl.hasAttribute("data-bp-last-name") ||
      rawEl.hasAttribute("data-bitpalm-last-name") ||
      rawEl.hasAttribute("data-bp-company") ||
      rawEl.hasAttribute("data-bitpalm-company") ||
      !!fieldHint;

    if (rawEl instanceof HTMLInputElement && rawEl.type.toLowerCase() === "hidden" && !explicitField) continue;

    const value = normalizeText(rawEl.value);
    if (!value) continue;

    const type = rawEl instanceof HTMLInputElement ? rawEl.type.toLowerCase() : "";
    const tokens = normalizeTokens(collectElementTokenParts(form, rawEl).join(" "));

    if (!email && fieldHint === "email" && isEmail(value)) {
      email = value.toLowerCase();
      continue;
    }
    if (!phone && (fieldHint === "phone" || fieldHint === "tel" || fieldHint === "mobile" || fieldHint === "whatsapp")) {
      const normalized = normalizePhone(value);
      if (normalized) {
        phone = normalized;
        continue;
      }
    }
    if (!company && fieldHint === "company") {
      company = normalizeText(value, 160);
      continue;
    }
    if (!firstName && (fieldHint === "first-name" || fieldHint === "firstname")) {
      firstName = normalizeText(value, 100);
      continue;
    }
    if (!lastName && (fieldHint === "last-name" || fieldHint === "lastname" || fieldHint === "surname")) {
      lastName = normalizeText(value, 100);
      continue;
    }
    if (!name && (fieldHint === "name" || fieldHint === "full-name" || fieldHint === "fullname")) {
      if (!isEmail(value) && !normalizePhone(value)) name = normalizeText(value, 140);
      continue;
    }

    const emailLike = type === "email" || includesAny(tokens, [" email ", " e mail ", " mail ", " email adresse ", " mailadresse ", " correo "]);
    const phoneLike = type === "tel" || includesAny(tokens, [" phone ", " phone number ", " mobile ", " tel ", " telefon ", " telefonnummer ", " whatsapp ", " handy ", " kontakt ", " nummer "]);
    const companyLike = includesAny(tokens, [" company ", " company name ", " firma ", " firmenname ", " organization ", " organisation ", " business ", " unternehmen ", " agency ", " brokerage "]);
    const firstNameLike = includesAny(tokens, [" first name ", " firstname ", " given name ", " givenname ", " vorname ", " fname "]);
    const lastNameLike = includesAny(tokens, [" last name ", " lastname ", " family name ", " familyname ", " surname ", " nachname ", " lname "]);
    const fullNameLike = includesAny(tokens, [" full name ", " fullname ", " contact name ", " name "]) && !emailLike && !phoneLike;

    if (!email && emailLike && isEmail(value)) {
      email = value.toLowerCase();
      continue;
    }
    if (!phone && phoneLike) {
      const normalized = normalizePhone(value);
      if (normalized) {
        phone = normalized;
        continue;
      }
    }
    if (!company && companyLike) {
      company = normalizeText(value, 160);
      continue;
    }
    if (!firstName && firstNameLike) {
      firstName = normalizeText(value, 100);
      continue;
    }
    if (!lastName && lastNameLike) {
      lastName = normalizeText(value, 100);
      continue;
    }
    if (!name && fullNameLike && !isEmail(value) && !normalizePhone(value)) {
      name = normalizeText(value, 140);
      continue;
    }

    if (!email && isEmail(value)) {
      email = value.toLowerCase();
    }
  }

  if (name && (isEmail(name) || normalizePhone(name))) {
    name = "";
  }
  if (company && (isEmail(company) || normalizePhone(company))) {
    company = "";
  }

  if (!name) {
    name = normalizeText([firstName, lastName].filter(Boolean).join(" "), 140);
  }

  if (name && email && name.toLowerCase() === email.toLowerCase()) {
    name = "";
  }

  const hasContactSignal = !!(email || phone);
  const hasStrongProfileSignal = !!(name && company);
  if (!hasContactSignal && !hasStrongProfileSignal) {
    return null;
  }

  const identity: IdentityPayload = {
    ...(name ? { name } : {}),
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(company ? { company } : {}),
  };

  return Object.keys(identity).length > 0 ? identity : null;
}

function detectLocale(): string {
  if (typeof document !== "undefined") {
    const lang = document.documentElement.lang;
    if (lang) return lang.slice(0, 2).toLowerCase();
  }
  return "en";
}

function runWhenIdle(task: () => void, timeout = 1200) {
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    (
      window as unknown as {
        requestIdleCallback: (cb: () => void, opts?: { timeout?: number }) => number;
      }
    ).requestIdleCallback(task, { timeout });
    return;
  }
  setTimeout(task, Math.min(timeout, 300));
}

function isMobileWidgetDevice(): boolean {
  if (typeof window === "undefined") return false;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isMobileUa = /Android|iPhone|iPad|iPod|IEMobile|Windows Phone|Mobile/i.test(userAgent);
  if (isMobileUa) return true;

  const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
  const noHover = window.matchMedia("(hover: none)").matches;
  const minScreenSide = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight,
  );
  return coarsePointer && noHover && minScreenSide <= 900;
}

export function createWidget(options: BitPalmAgentOptions): BitPalmAgentInstance {
  const {
    slug,
    locale = detectLocale(),
    baseUrl = DEFAULT_BASE_URL,
    token,
    zIndex = 9999,
    autoIdentify = true,
  } = options;

  const d = document;

  // --- Iframe ---
  const iframe = d.createElement("iframe");
  const widgetUrl = `${baseUrl}/t/${slug}/widget`;
  const qs = new URLSearchParams({ locale });
  if (token) qs.set("token", token);
  iframe.src = `${widgetUrl}?${qs.toString()}`;
  iframe.allow = "clipboard-write";
  iframe.setAttribute("allowtransparency", "true");
  iframe.loading = "lazy";
  iframe.style.cssText = `position:fixed;bottom:0;right:0;width:1px;height:1px;border:none;z-index:${zIndex};background:transparent;color-scheme:normal;opacity:0;pointer-events:none;`;

  const origin = new URL(iframe.src).origin;

  // --- Native launcher button ---
  const btn = d.createElement("div");
  btn.setAttribute("role", "button");
  btn.setAttribute("aria-label", "Chat");
  btn.style.cssText = `position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;display:none;align-items:center;justify-content:center;cursor:pointer;z-index:${zIndex - 1};background:#00ff41;box-shadow:0 0 15px rgba(0,255,65,0.31);transition:transform .2s,opacity .3s;opacity:0;`;
  btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>';
  btn.onmouseenter = () => { btn.style.transform = "scale(1.1)"; };
  btn.onmouseleave = () => { btn.style.transform = "scale(1)"; };
  btn.onclick = () => openWidget();
  d.body.appendChild(btn);

  // --- Fade animation for trigger bubbles ---
  if (!d.getElementById("_bp_fade")) {
    const s = d.createElement("style");
    s.id = "_bp_fade";
    s.textContent = "@keyframes _bp_fade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}";
    d.head.appendChild(s);
  }

  // --- Trigger bubble ---
  let bubble: HTMLDivElement | null = null;
  let launcherReady = false;
  let widgetBlocked = false;
  let iframeMounted = false;
  let iframeReady = false;
  let pendingOpen = false;
  const mountIframe = () => {
    if (iframeMounted) return;
    iframeMounted = true;
    d.body.appendChild(iframe);
  };
  let mobileScrollLocked = false;
  let scrollLockY = 0;
  const prevHtmlStyle = {
    overflow: "",
    overscrollBehavior: "",
  };
  const prevBodyStyle = {
    overflow: "",
    overscrollBehavior: "",
    position: "",
    top: "",
    left: "",
    right: "",
    width: "",
  };
  const lockMobilePageScroll = () => {
    if (mobileScrollLocked) return;
    const mobile = isMobileWidgetDevice();
    if (!mobile) return;
    const html = d.documentElement;
    const body = d.body;
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    prevHtmlStyle.overflow = html.style.overflow;
    prevHtmlStyle.overscrollBehavior = html.style.overscrollBehavior;
    prevBodyStyle.overflow = body.style.overflow;
    prevBodyStyle.overscrollBehavior = body.style.overscrollBehavior;
    prevBodyStyle.position = body.style.position;
    prevBodyStyle.top = body.style.top;
    prevBodyStyle.left = body.style.left;
    prevBodyStyle.right = body.style.right;
    prevBodyStyle.width = body.style.width;
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.position = "fixed";
    body.style.top = `-${scrollLockY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    mobileScrollLocked = true;
  };
  const unlockMobilePageScroll = () => {
    if (!mobileScrollLocked) return;
    const html = d.documentElement;
    const body = d.body;
    html.style.overflow = prevHtmlStyle.overflow;
    html.style.overscrollBehavior = prevHtmlStyle.overscrollBehavior;
    body.style.overflow = prevBodyStyle.overflow;
    body.style.overscrollBehavior = prevBodyStyle.overscrollBehavior;
    body.style.position = prevBodyStyle.position;
    body.style.top = prevBodyStyle.top;
    body.style.left = prevBodyStyle.left;
    body.style.right = prevBodyStyle.right;
    body.style.width = prevBodyStyle.width;
    window.scrollTo(0, scrollLockY);
    mobileScrollLocked = false;
  };
  const prepareIframeForOpen = () => {
    const mobile = isMobileWidgetDevice();
    if (iframe.style.display === "none") iframe.style.display = "";
    if (mobile) {
      iframe.style.width = "100vw";
      iframe.style.height = "100dvh";
      iframe.style.left = "0";
      iframe.style.top = "0";
      iframe.style.bottom = "0";
      iframe.style.right = "0";
      lockMobilePageScroll();
    } else {
      // Match desktop widget target size so the opening animation is visible from frame 1.
      iframe.style.width = "420px";
      iframe.style.height = "620px";
      iframe.style.bottom = "24px";
      iframe.style.right = "24px";
      iframe.style.left = "";
      iframe.style.top = "";
      unlockMobilePageScroll();
    }
    iframe.style.opacity = "1";
    iframe.style.pointerEvents = "auto";
  };
  const removeBubble = () => {
    if (bubble) { bubble.remove(); bubble = null; }
  };
  let sendParentContext = () => {};
  let sendOpenSignal = () => {};

  // --- Widget state & event bus ---
  let widgetIsOpen = false;
  const eventListeners = new Map<BitPalmEventType, Set<BitPalmEventCallback>>();

  function emit(event: BitPalmEventType, data?: Record<string, unknown>) {
    const listeners = eventListeners.get(event);
    if (!listeners) return;
    for (const cb of listeners) {
      try { cb(data); } catch { /* don't let listener errors break the widget */ }
    }
  }

  // --- postMessage handler ---
  const handleMessage = (e: MessageEvent) => {
    if (e.origin !== origin) return;

    // Widget config (colors, icon)
    if (e.data?.type === "bitpalm-widget-config") {
      widgetBlocked = false;
      launcherReady = true;
      if (iframe.style.display === "none") iframe.style.display = "";
      if (e.data.primaryColor) {
        btn.style.background = e.data.primaryColor;
        btn.style.boxShadow = e.data.chatShadow === "none" ? "none" : `0 0 15px ${e.data.primaryColor}50`;
      }
      const ic = e.data.chatButtonIconColor || "black";
      if (e.data.chatButtonIconSvg) {
        btn.innerHTML = "";
        const sp = d.createElement("span");
        sp.style.cssText = `display:flex;align-items:center;justify-content:center;width:24px;height:24px;color:${ic}`;
        const parser = new DOMParser();
        const doc = parser.parseFromString(e.data.chatButtonIconSvg, "image/svg+xml");
        const svg = doc.querySelector("svg");
        if (svg) {
          // Remove dangerous elements and attributes
          svg.querySelectorAll("script,foreignObject").forEach(el => el.remove());
          svg.querySelectorAll("*").forEach(el => {
            for (const attr of [...el.attributes]) {
              if (attr.name.startsWith("on") || (attr.value && attr.value.includes("javascript:"))) {
                el.removeAttribute(attr.name);
              }
            }
          });
          svg.setAttribute("width", "24");
          svg.setAttribute("height", "24");
          svg.style.display = "block";
          sp.appendChild(svg);
        } else {
          // Fallback: use default icon
          sp.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>';
        }
        btn.appendChild(sp);
      } else {
        const sv = btn.querySelector("svg");
        if (sv) (sv as SVGElement).style.stroke = ic;
      }
      btn.style.display = "flex";
      // Fade in after styles are applied
      requestAnimationFrame(() => { btn.style.opacity = "1"; });
    }

    // Resize — use full viewport on mobile
    if (e.data?.type === "bitpalm-widget-resize") {
      const isOpen = e.data.width > 0;
      const wasOpen = widgetIsOpen;
      widgetIsOpen = isOpen;
      if (isOpen && !wasOpen) emit("widget_opened");
      if (!isOpen && wasOpen) emit("widget_closed");
      const mobile = isMobileWidgetDevice();
      if (isOpen) {
        iframe.style.width = mobile ? "100vw" : `${e.data.width}px`;
        iframe.style.height = mobile ? "100dvh" : `${e.data.height}px`;
        if (mobile) {
          iframe.style.left = "0";
          iframe.style.top = "0";
          iframe.style.bottom = "0";
          iframe.style.right = "0";
          lockMobilePageScroll();
        } else {
          unlockMobilePageScroll();
        }
        iframe.style.opacity = "1";
        iframe.style.pointerEvents = "auto";
      } else {
        unlockMobilePageScroll();
        iframe.style.width = "1px";
        iframe.style.height = "1px";
        iframe.style.left = "";
        iframe.style.top = "";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
      }
      btn.style.display = isOpen ? "none" : "flex";
    }

    // Trigger bubble show
    if (e.data?.type === "bitpalm-trigger-show") {
      if (widgetBlocked || !launcherReady) return;
      removeBubble();
      bubble = d.createElement("div");
      bubble.style.cssText = `position:fixed;bottom:90px;right:24px;max-width:240px;background:#fff;border-radius:16px;padding:12px 32px 12px 12px;box-shadow:0 2px 20px rgba(0,0,0,0.08);cursor:pointer;z-index:${zIndex - 1};font-family:system-ui,sans-serif;animation:_bp_fade .3s;`;
      const msg = (e.data.message || "").replace(/</g, "&lt;");
      bubble.innerHTML = `<p style="font-size:14px;color:#1f2937;margin:0;">${msg}</p><div style="position:absolute;bottom:-8px;right:20px;width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:8px solid #fff;"></div>`;
      const close = d.createElement("button");
      close.style.cssText = "position:absolute;top:-8px;right:-8px;width:28px;height:28px;border-radius:50%;background:#fff;border:none;box-shadow:0 1px 4px rgba(0,0,0,0.1);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;color:#9ca3af;";
      close.innerHTML = "&#10005;";
      close.onclick = (ev) => { ev.stopPropagation(); removeBubble(); iframe.contentWindow?.postMessage({ type: "bitpalm-trigger-dismiss" }, "*"); };
      bubble.onclick = () => {
        removeBubble();
        btn.style.display = "none";
        prepareIframeForOpen();
        mountIframe();
        if (!iframeReady) {
          pendingOpen = true;
          return;
        }
        iframe.contentWindow?.postMessage({ type: "bitpalm-trigger-engaged", message: msg }, "*");
      };
      bubble.appendChild(close);
      d.body.appendChild(bubble);
    }

    // Trigger bubble hide
    if (e.data?.type === "bitpalm-trigger-hide") { removeBubble(); }

    // Iframe requests latest parent context (avoids first-load race conditions)
    if (e.data?.type === "bitpalm-request-context") {
      sendParentContext();
    }

    // Chat events from iframe → emit to SDK listeners
    if (e.data?.type === "bitpalm-chat-event" && e.data.event) {
      emit(e.data.event as BitPalmEventType, e.data.data);
    }

    // Widget blocked (excluded path or access denied) — hide button & iframe
    if (e.data?.type === "bitpalm-widget-blocked") {
      widgetBlocked = true;
      launcherReady = false;
      btn.style.display = "none";
      removeBubble();
      unlockMobilePageScroll();
      iframe.style.display = "none";
    }
  };
  window.addEventListener("message", handleMessage);

  // --- Privacy opt-out check ---
  let optedOut = false;
  try { optedOut = localStorage.getItem(`${STORAGE_PREFIX}optout`) === "1"; } catch { /* ignore */ }

  // --- Persistent Visitor ID (skip if opted out) ---
  const VID_KEY = `${STORAGE_PREFIX}vid`;
  let visitorId = "";
  if (!optedOut) {
    try { visitorId = localStorage.getItem(VID_KEY) || ""; } catch { /* ignore */ }
    if (!visitorId) {
      // Fallback: try reading from cookie
      const match = d.cookie.match(new RegExp(`(?:^|; )${VID_KEY}=([^;]+)`));
      visitorId = match ? decodeURIComponent(match[1]) : "";
    }
    if (!visitorId) {
      visitorId = crypto.randomUUID ? crypto.randomUUID() : (`${Date.now()}-${Math.random().toString(36).slice(2, 11)}`);
    }
    // Persist in both localStorage and cookie (cookie survives Safari ITP clearing localStorage)
    try { localStorage.setItem(VID_KEY, visitorId); } catch { /* ignore */ }
    try {
      const maxAge = 365 * 24 * 60 * 60; // 1 year
      d.cookie = `${VID_KEY}=${encodeURIComponent(visitorId)};path=/;max-age=${maxAge};SameSite=Lax`;
    } catch { /* ignore */ }
  }

  // --- Visitor beacon: track page view even without chat (skip if opted out) ---
  if (!optedOut) {
    runWhenIdle(() => {
      try {
        const utmParams = new URLSearchParams(location.search);
        const utm: Record<string, string> = {};
        for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
          const val = utmParams.get(key);
          if (val) utm[key] = val;
        }
        const beaconPayload = JSON.stringify({
          tenant_slug: slug,
          visitor_id: visitorId,
          url: location.href,
          referrer: d.referrer || undefined,
          ...(Object.keys(utm).length > 0 ? { utm } : {}),
        });
        const beaconUrl = `${baseUrl}/api/widget/impression`;
        if (typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(beaconUrl, new Blob([beaconPayload], { type: "application/json" }));
        } else {
          fetch(beaconUrl, { method: "POST", body: beaconPayload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
        }
      } catch { /* ignore */ }
    }, 2000);
  }

  // --- Auto-identify visitor from form submissions on host page ---
  let lastIdentitySignature = "";
  let lastIdentityFingerprint = "";
  let lastIdentitySentAt = 0;
  try {
    lastIdentitySignature = sessionStorage.getItem(IDENTITY_SIGNATURE_KEY) || "";
    lastIdentityFingerprint = sessionStorage.getItem(IDENTITY_FINGERPRINT_KEY) || "";
    const storedAt = parseInt(sessionStorage.getItem(IDENTITY_SENT_AT_KEY) || "0", 10);
    if (Number.isFinite(storedAt) && storedAt > 0) {
      lastIdentitySentAt = storedAt;
    }
  } catch {
    // ignore
  }

  const dispatchIdentify = (payloadInput: IdentifyRequestPayload, scopeSignature = "") => {
    if (optedOut || !visitorId) return;

    const payload: IdentifyRequestPayload = {};
    const name = normalizeText(payloadInput.name, 140);
    const email = normalizeEmail(payloadInput.email);
    const phone = normalizePhone(payloadInput.phone);
    const company = normalizeText(payloadInput.company, 160);
    const externalUserId = normalizeText(payloadInput.external_user_id, 140);
    const requestSignature = normalizeText(payloadInput.signature, 260);
    const signedAtRaw = Number(payloadInput.signed_at);

    if (name) payload.name = name;
    if (email) payload.email = email;
    if (phone) payload.phone = phone;
    if (company) payload.company = company;
    if (externalUserId) payload.external_user_id = externalUserId;
    if (requestSignature) payload.signature = requestSignature;
    if (Number.isFinite(signedAtRaw) && signedAtRaw > 0) {
      payload.signed_at = Math.floor(signedAtRaw);
    }
    if (payloadInput.signature_version === "v1" || payload.signature) {
      payload.signature_version = "v1";
    }
    if (payloadInput.identify_source === "manual" || payloadInput.identify_source === "form") {
      payload.identify_source = payloadInput.identify_source;
    }

    if (!payload.name && !payload.email && !payload.phone && !payload.company && !payload.external_user_id) {
      return;
    }

    const identityFingerprint = buildIdentityFingerprint(payload);
    const dedupeSignature = [scopeSignature.toLowerCase(), identityFingerprint].filter(Boolean).join("|");

    const now = Date.now();
    const insideDedupWindow = now - lastIdentitySentAt < IDENTITY_DEDUPE_WINDOW_MS;
    if (insideDedupWindow && (dedupeSignature === lastIdentitySignature || identityFingerprint === lastIdentityFingerprint)) {
      return;
    }

    lastIdentitySignature = dedupeSignature;
    lastIdentityFingerprint = identityFingerprint;
    lastIdentitySentAt = now;
    try {
      sessionStorage.setItem(IDENTITY_SIGNATURE_KEY, dedupeSignature);
      sessionStorage.setItem(IDENTITY_FINGERPRINT_KEY, identityFingerprint);
      sessionStorage.setItem(IDENTITY_SENT_AT_KEY, String(now));
    } catch {
      // ignore
    }

    try {
      const requestBody = JSON.stringify({
        tenant_slug: slug,
        visitor_id: visitorId,
        url: location.href,
        referrer: d.referrer || undefined,
        ...payload,
      });
      const identifyUrl = `${baseUrl}/api/widget/identify`;
      if (typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(identifyUrl, new Blob([requestBody], { type: "application/json" }));
      } else {
        fetch(identifyUrl, { method: "POST", body: requestBody, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
      }
    } catch {
      // ignore
    }
  };

  const handleFormSubmit = (event: Event) => {
    if (optedOut || !autoIdentify || !visitorId) return;

    const form = event.target instanceof HTMLFormElement
      ? event.target
      : event.target instanceof Element
        ? event.target.closest("form")
        : null;
    if (!(form instanceof HTMLFormElement)) return;

    const formIdentifySetting = (form.getAttribute("data-bp-identify") || form.getAttribute("data-bitpalm-identify") || "").toLowerCase();
    if (formIdentifySetting === "off" || formIdentifySetting === "false" || formIdentifySetting === "0") return;

    const identity = extractIdentityFromForm(form);
    if (!identity) return;

    const formSignature = [
      form.getAttribute("id") || "",
      form.getAttribute("name") || "",
      form.getAttribute("action") || "",
    ].join("|");

    dispatchIdentify({ ...identity, identify_source: "form" }, formSignature);
  };

  if (!optedOut && autoIdentify) {
    d.addEventListener("submit", handleFormSubmit, true);
  }

  // --- Visitor Intelligence: page counter + history ---
  let pageCount = parseInt(sessionStorage.getItem(`${STORAGE_PREFIX}pages`) || "0") + 1;
  sessionStorage.setItem(`${STORAGE_PREFIX}pages`, String(pageCount));

  let hist: string[] = JSON.parse(sessionStorage.getItem(`${STORAGE_PREFIX}hist`) || "[]");
  hist.push(location.href);
  if (hist.length > 20) hist = hist.slice(-20);
  sessionStorage.setItem(`${STORAGE_PREFIX}hist`, JSON.stringify(hist));

  const loadedAt = Date.now();
  let siteStart = parseInt(sessionStorage.getItem(`${STORAGE_PREFIX}site_start`) || "0");
  if (!siteStart) {
    siteStart = loadedAt;
    sessionStorage.setItem(`${STORAGE_PREFIX}site_start`, String(siteStart));
  }

  const contextRetryTimers: ReturnType<typeof setTimeout>[] = [];
  sendParentContext = () => {
    let returning = false;
    try {
      returning = !!localStorage.getItem(`${STORAGE_PREFIX}visited`);
    } catch {
      returning = false;
    }
    iframe.contentWindow?.postMessage({
      type: "bitpalm-parent-context",
      url: location.href,
      title: d.title,
      returning,
      pageCount,
      pageHistory: hist,
      loadedAt,
      siteStart,
      locale,
      visitorId: optedOut ? "" : visitorId,
    }, "*");
    if (!returning && !optedOut) {
      try { localStorage.setItem(`${STORAGE_PREFIX}visited`, "1"); } catch { /* ignore */ }
    }
  };

  // --- Send parent context on iframe load ---
  iframe.addEventListener("load", () => {
    iframeReady = true;
    sendParentContext();
    [120, 450, 1100].forEach((delay) => {
      const timer = setTimeout(() => sendParentContext(), delay);
      contextRetryTimers.push(timer);
    });
    if (pendingOpen) {
      pendingOpen = false;
      sendOpenSignal();
    }
  });

  // Lazy-mount iframe after initial paint/idle to reduce impact on host load.
  if (d.readyState === "loading") {
    d.addEventListener("DOMContentLoaded", () => runWhenIdle(mountIframe, 1600), { once: true });
  } else {
    runWhenIdle(mountIframe, 1600);
  }

  // --- SPA navigation detection ---
  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  const onNav = () => {
    pageCount++;
    sessionStorage.setItem(`${STORAGE_PREFIX}pages`, String(pageCount));
    const h: string[] = JSON.parse(sessionStorage.getItem(`${STORAGE_PREFIX}hist`) || "[]");
    h.push(location.href);
    if (h.length > 20) h.splice(0, h.length - 20);
    hist = h;
    sessionStorage.setItem(`${STORAGE_PREFIX}hist`, JSON.stringify(h));
    iframe.contentWindow?.postMessage({
      type: "bitpalm-page-change",
      url: location.href,
      title: d.title,
      pageCount,
      pageHistory: h,
      loadedAt: Date.now(),
    }, "*");
    // Track page view for visitor (skip if opted out)
    if (!optedOut) {
      try {
        const navPayload = JSON.stringify({ tenant_slug: slug, visitor_id: visitorId, url: location.href });
        const navUrl = `${baseUrl}/api/widget/impression`;
        if (typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(navUrl, new Blob([navPayload], { type: "application/json" }));
        } else {
          fetch(navUrl, { method: "POST", body: navPayload, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => {});
        }
      } catch { /* ignore */ }
    }
  };

  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    origPush(...args);
    onNav();
  };
  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    origReplace(...args);
    onNav();
  };
  window.addEventListener("popstate", onNav);

  // --- Scroll depth tracking ---
  let lastScrollDepth = -1;
  let scrollTimeout: ReturnType<typeof setTimeout>;
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const total = Math.max(d.documentElement.scrollHeight, d.body.scrollHeight) - window.innerHeight;
      const pct = total > 0 ? Math.round(window.scrollY / total * 100) : 100;
      if (pct !== lastScrollDepth) {
        lastScrollDepth = pct;
        iframe.contentWindow?.postMessage({ type: "bitpalm-scroll-depth", percent: pct }, "*");
      }
    }, 500);
  };
  window.addEventListener("scroll", handleScroll, { passive: true });

  // --- Exit intent ---
  const handleMouseOut = (e: MouseEvent) => {
    if (e.clientY <= 0) {
      iframe.contentWindow?.postMessage({ type: "bitpalm-exit-intent" }, "*");
    }
  };
  d.addEventListener("mouseout", handleMouseOut);

  // --- Inactivity detection (15s) ---
  let idleTimer: ReturnType<typeof setTimeout>;
  let idleFired = false;
  const resetIdle = () => {
    clearTimeout(idleTimer);
    if (idleFired) return;
    idleTimer = setTimeout(() => {
      idleFired = true;
      iframe.contentWindow?.postMessage({ type: "bitpalm-inactivity" }, "*");
    }, 15_000);
  };
  const idleEvents = ["mousemove", "keydown", "scroll", "touchstart"] as const;
  idleEvents.forEach((ev) => d.addEventListener(ev, resetIdle, { passive: true }));
  resetIdle();

  sendOpenSignal = () => {
    // Primary: postMessage
    iframe.contentWindow?.postMessage({ type: "bitpalm-open" }, "*");
    // Fallback for mobile: trigger hashchange inside iframe (no reload)
    const base = iframe.src.split("#")[0];
    iframe.src = `${base}#bp-open-${Date.now()}`;
  };

  // --- Public API ---
  const openWidget = () => {
    btn.style.display = "none";
    removeBubble();
    prepareIframeForOpen();
    mountIframe();
    if (!iframeReady) {
      pendingOpen = true;
      return;
    }
    sendOpenSignal();
  };

  const closeWidget = () => {
    unlockMobilePageScroll();
    iframe.contentWindow?.postMessage({ type: "bitpalm-close" }, "*");
  };

  const identify = (input: BitPalmIdentifyPayload) => {
    if (!input || typeof input !== "object") return;
    dispatchIdentify({
      external_user_id: normalizeText(input.externalUserId, 140) || undefined,
      name: normalizeText(input.name, 140) || undefined,
      email: normalizeEmail(input.email) || undefined,
      phone: normalizePhone(input.phone) || undefined,
      company: normalizeText(input.company, 160) || undefined,
      signature: normalizeText(input.signature, 260) || undefined,
      signed_at: Number.isFinite(Number(input.signedAt)) ? Math.floor(Number(input.signedAt)) : undefined,
      signature_version: input.signatureVersion === "v1" ? "v1" : undefined,
      identify_source: "manual",
    }, "manual");
  };

  const toggleWidget = () => {
    if (widgetIsOpen) closeWidget();
    else openWidget();
  };

  const getIsOpen = () => widgetIsOpen;

  const sendMessage = (message: string) => {
    if (!message || typeof message !== "string") return;
    const text = message.trim().slice(0, 10_000);
    if (!text) return;
    if (!widgetIsOpen) openWidget();
    // Small delay to ensure widget is open before sending
    setTimeout(() => {
      iframe.contentWindow?.postMessage({ type: "bitpalm-send-message", message: text }, "*");
    }, widgetIsOpen ? 50 : 500);
  };

  // --- Custom context for AI ---
  let customContext: Record<string, unknown> = {};

  const setCustomContext = (data: BitPalmCustomContext) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return;
    customContext = { ...customContext, ...data };
    const json = JSON.stringify(customContext);
    if (json.length > CUSTOM_CONTEXT_MAX_BYTES) {
      console.warn(
        `[BitPalm] Custom context exceeds ${CUSTOM_CONTEXT_MAX_BYTES} bytes (${json.length}). It may be truncated.`
      );
    }
    iframe.contentWindow?.postMessage({ type: "bitpalm-custom-context", data: customContext }, "*");
  };

  const clearCustomContext = () => {
    customContext = {};
    iframe.contentWindow?.postMessage({ type: "bitpalm-custom-context", data: {} }, "*");
  };

  const handleContextEvent = (event: Event) => {
    const customEvent = event as CustomEvent<BitPalmCustomContext & { slug?: string }>;
    const detail = customEvent.detail;
    if (!detail || typeof detail !== "object") return;
    const targetSlug = normalizeText((detail as Record<string, unknown>).slug as string, 120);
    if (targetSlug && targetSlug !== slug) return;
    const { slug: _s, ...contextData } = detail as Record<string, unknown>;
    setCustomContext(contextData);
  };

  window.addEventListener("bitpalm-context", handleContextEvent as EventListener);

  const handleIdentifyEvent = (event: Event) => {
    const customEvent = event as CustomEvent<BitPalmIdentifyPayload & { slug?: string }>;
    const detail = customEvent.detail;
    if (!detail || typeof detail !== "object") return;
    const targetSlug = normalizeText(detail.slug, 120);
    if (targetSlug && targetSlug !== slug) return;
    identify(detail);
  };

  window.addEventListener("bitpalm-identify", handleIdentifyEvent as EventListener);

  const onEvent = (event: BitPalmEventType, callback: BitPalmEventCallback): (() => void) => {
    if (!eventListeners.has(event)) eventListeners.set(event, new Set());
    eventListeners.get(event)!.add(callback);
    return () => eventListeners.get(event)?.delete(callback);
  };

  const offEvent = (event: BitPalmEventType, callback: BitPalmEventCallback) => {
    eventListeners.get(event)?.delete(callback);
  };

  const destroy = () => {
    window.removeEventListener("message", handleMessage);
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("popstate", onNav);
    d.removeEventListener("mouseout", handleMouseOut);
    d.removeEventListener("submit", handleFormSubmit, true);
    idleEvents.forEach((ev) => d.removeEventListener(ev, resetIdle));
    clearTimeout(scrollTimeout);
    clearTimeout(idleTimer);
    contextRetryTimers.forEach((t) => clearTimeout(t));
    history.pushState = origPush;
    history.replaceState = origReplace;
    window.removeEventListener("bitpalm-identify", handleIdentifyEvent as EventListener);
    window.removeEventListener("bitpalm-context", handleContextEvent as EventListener);
    eventListeners.clear();
    unlockMobilePageScroll();
    removeBubble();
    btn.remove();
    iframe.remove();
  };

  return {
    open: openWidget,
    close: closeWidget,
    toggle: toggleWidget,
    isOpen: getIsOpen,
    identify,
    context: setCustomContext,
    resetContext: clearCustomContext,
    send: sendMessage,
    on: onEvent,
    off: offEvent,
    destroy,
  };
}
